import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useNeedsAttention } from "@/hooks/useNeedsAttention";
import QLogo from "@/components/QLogo";
import {
  Tag, Lightning, Bell, Trophy, ChartLineUp, ShoppingBag,
  SignOut, House, List, X, Users as UsersIcon, ChatCircle, WhatsappLogo, Robot
} from "@phosphor-icons/react";

import SellerLeads from "./admin/SellerLeads";
import BuyerReqs from "./admin/BuyerReqs";
import Matching from "./admin/Matching";
import LiveOffersAdmin from "./admin/LiveOffersAdmin";
import Notifications from "./admin/Notifications";
import Inspections from "./admin/Inspections";
import Analytics from "./admin/Analytics";
import Acquisitions from "./admin/Acquisitions";
import Users from "./admin/Users";
import CrmLeads from "./admin/CrmLeads";
import CrmLeadDetail from "./admin/CrmLeadDetail";
import AdminWaCloud from "./admin/AdminWaCloud";
import WhatsAppQaDashboard from "./admin/WhatsAppQaDashboard";

// Sidebar nav — Buyer Requirements is a first-class operational queue.
// `superOnly` items only render for the Super Admin (User Management).
const NAV = [
  { to: "/admin/leads", label: "Leads", icon: Tag, key: "seller_leads", end: true },
  { to: "/admin/crm", label: "CRM Leads", icon: ChatCircle, key: null },
  { to: "/admin/buyer-requirements", label: "Buyer Requirements", icon: ShoppingBag, key: "buyer_requirements" },
  { to: "/admin/negotiations", label: "Negotiations", icon: Lightning, key: null },
  { to: "/admin/acquired", label: "Acquired", icon: Trophy, key: null },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, key: null },
  { to: "/admin/analytics", label: "Analytics", icon: ChartLineUp, key: null },
  { to: "/admin/wa-cloud", label: "WhatsApp Cloud", icon: WhatsappLogo, key: null, superOnly: true },
  { to: "/admin/wa-qa", label: "WhatsApp QA", icon: Robot, key: null, superOnly: true },
  { to: "/admin/users", label: "Team", icon: UsersIcon, key: null, superOnly: true },
];

export default function Admin() {
  const nav = useNavigate();
  const loc = useLocation();
  const { logout, user } = useAuth();
  const [counts, setCounts] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Phase 10.6b — global Needs-Attention poller (toast on count increase).
  const { count: needsAttentionCount } = useNeedsAttention();


  const loadCounts = async () => {
    try { const { data } = await api.get("/admin/unread-counts"); setCounts(data); } catch (err) { console.error("Unread counts load failed:", err); }
  };

  // Load counts on mount, refresh every 30s
  useEffect(() => {
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    return () => clearInterval(t);
  }, []);

  // Mark-seen whenever route changes (clears badge for the page just visited)
  useEffect(() => {
    api.post("/admin/mark-seen").then(loadCounts).catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]" data-testid="admin-page">
      <aside
        data-testid="admin-sidebar"
        className={`
          fixed md:sticky top-0 left-0 z-50 h-screen w-56 border-r border-[#2A2A2A]
          flex flex-col qd-pt-safe-header pb-5 px-3 bg-[#0A0A0A]
          transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        <div className="mb-6 px-2 flex items-center justify-between">
          <QLogo size={28} />
          <button
            data-testid="admin-mobile-close-btn"
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1 text-white/55 active:text-white"
            aria-label="Close menu"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="qd-overline mb-3 px-2 text-[10px]">Command Center</div>
        <nav className="flex flex-col gap-0.5">
          {NAV.filter(n => !n.superOnly || user?.is_super_admin).map((n) => {
            const baseBadge = n.key ? counts[n.key] : 0;
            // Phase 10.6b — surface Needs-Attention count on the CRM Leads item.
            const badge = n.to === "/admin/crm"
              ? Math.max(baseBadge || 0, needsAttentionCount || 0)
              : baseBadge;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold transition-colors ${
                    isActive ? "text-[#FF1E1E] bg-[#FF1E1E]/10 border-l-2 border-[#FF1E1E]" : "text-white/55 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <n.icon size={14} weight="bold" />
                <span className="flex-1">{n.label}</span>
                {badge > 0 && (
                  <span data-testid={`badge-${n.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-[9px] font-mono-data tracking-normal bg-[#FF1E1E] text-white px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto pt-4 border-t border-[#2A2A2A]">
          <div className="px-3 py-2 text-[10px] font-mono-data tracking-widest text-white/40 uppercase">
            {user?.phone}
          </div>
          <a
            href="tel:+918977986662"
            data-testid="admin-ops-contact"
            className="block px-3 py-2 text-[10px] tracking-wide text-[#E55A5A] hover:text-[#FF1E1E]"
          >
            Ops Hotline · +91 89779 86662
          </a>
          <button data-testid="admin-go-home" className="flex items-center gap-3 px-3 py-2 text-[11px] font-display tracking-widest uppercase text-white/55 hover:text-white" onClick={() => nav("/")}>
            <House size={14} /> Consumer App
          </button>
          <button data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2 text-[11px] font-display tracking-widest uppercase text-white/55 hover:text-white" onClick={() => { logout(); nav("/login"); }}>
            <SignOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-3 sm:p-6 lg:p-8 overflow-x-clip min-w-0">
        {/* Mobile-only top bar with hamburger toggle */}
        <div className="md:hidden flex items-center justify-between mb-3 qd-pt-safe-header-lg">
          <button
            data-testid="admin-mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 text-white/85 active:bg-white/5"
            aria-label="Open menu"
          >
            <List size={20} weight="bold" />
          </button>
          <QLogo size={22} />
          <div className="w-8" />
        </div>
        <Routes>
          {/* Leads is the operational home */}
          <Route index element={<SellerLeads />} />
          <Route path="leads" element={<SellerLeads />} />
          <Route path="negotiations" element={<SellerLeads initialStageFilter="negotiating" />} />
          <Route path="acquired" element={<Acquisitions />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="analytics" element={<Analytics />} />
          {/* Back-compat: legacy URLs still resolve (not in nav) */}
          <Route path="seller-leads" element={<SellerLeads />} />
          <Route path="acquisitions" element={<Acquisitions />} />
          <Route path="buyer-requirements" element={<BuyerReqs />} />
          <Route path="matching" element={<Matching />} />
          <Route path="live-offers" element={<LiveOffersAdmin />} />
          <Route path="inspections" element={<Inspections />} />
          <Route path="users" element={<Users />} />
          {/* CRM Leads — Phase 2 internal foundation (no WhatsApp / AI connected) */}
          <Route path="crm" element={<CrmLeads />} />
          <Route path="crm/:id" element={<CrmLeadDetail />} />
          <Route path="wa-cloud" element={<AdminWaCloud />} />
          <Route path="wa-qa" element={<WhatsAppQaDashboard />} />
        </Routes>
      </main>
    </div>
  );
}
